import React from 'react';
import PropTypes from 'prop-types';
import { Button } from 'react-bootstrap';
import AsyncFeedback from 'components/AsyncFeedback/AsyncFeedback';
import RedirectsEditModal from 'admin/containers/RedirectsEditModal';
import Authorization from 'containers/Authorization';
import { SEO_REDIRECTS, BUILDER } from 'shared/abilities';
import UnauthorizedBanner from '../UnauthorizedBanner';
import RedirectTable from './RedirectTable';
import style from './RedirectSettings.scss';

const prepareState = function ({ hostnames }) {
  return {
    // Create new objects to prevent mutating redux state
    hostnames: hostnames ? hostnames.map(obj => ({ ...obj })) : [],
    touchedHostnames: [],
    showUploadRedirects: false,
  };
};

export default class RedirectSettings extends React.Component {
  static propTypes = {
    hostnames: PropTypes.arrayOf(PropTypes.shape({
      hostname: PropTypes.string.isRequired,
      primary: PropTypes.bool.isRequired,
      redirects: PropTypes.arrayOf(PropTypes.shape({
        source: PropTypes.string.isRequired,
        target: PropTypes.string.isRequired,
      })).isRequired,
    })),
    onSave: PropTypes.func.isRequired,
    isSaving: PropTypes.bool.isRequired,
    error: PropTypes.array,
  };

  static contextTypes = {
    setTab: PropTypes.func.isRequired,
  };

  state = prepareState(this.props);

  UNSAFE_componentWillMount() {
    this.context.setTab('settings/redirects');
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (this.props.hostnames !== nextProps.hostnames) {
      this.setState(prepareState(nextProps));
    }
  }

  changeHostname(id, replacementFn) {
    this.setState(({ hostnames }) => ({
      hostnames: hostnames.map((hostname) => {
        if (hostname.id === id) {
          return {
            ...hostname,
            ...replacementFn(hostname),
          };
        }

        return hostname;
      }),
    }));
    this.touchHostname(id);
  }

  toggleUploadRedirects = () => {
    this.setState({ showUploadRedirects: !this.state.showUploadRedirects });
  }

  handleUpload = (uploadedRedirects) => {
    // TODO: Can remove this mapping when this container is using Apollo instead of the Redux store.
    const hostnames = [...this.state.hostnames];
    const touchedHostnames = [...this.state.touchedHostnames];

    const updatedHostnames = hostnames.map((hostname) => {
      // extract out the redirects from the upload that correspond to the hostname
      const matchingRedirects = uploadedRedirects
        .filter(uploadedRedirect => uploadedRedirect.hostname === hostname.hostname);

      hostname.redirects = matchingRedirects.reduce((existingRedirects, uploadedRedirect) => {
        // check if redirect already exists
        const match = hostname.redirects
          .find(existingRedirect =>
            existingRedirect.source === uploadedRedirect.source &&
            existingRedirect.target === uploadedRedirect.target);


        // if already exists, do nothing
        if (match) {
          return existingRedirects;
        }

        // otherwise, add it to the hostname
        const newRedirect = {
          source: uploadedRedirect.source,
          target: uploadedRedirect.target,
        };

        // mark the hostname as touched so it will be updated on save
        if (touchedHostnames.indexOf(hostname.id) === -1) {
          touchedHostnames.push(hostname.id);
        }

        return [
          ...existingRedirects,
          newRedirect,
        ];
      }, hostname.redirects);

      return hostname;
    });
    this.setState({ hostnames: updatedHostnames, touchedHostnames });
  }

  handleAdd = id => () => {
    this.changeHostname(id, hostname => ({
      redirects: [
        ...hostname.redirects,
        {
          source: '',
          target: '',
        },
      ],
    }));
  }

  handleChange = id => (index, newValues) => {
    this.changeHostname(id, (hostname) => {
      const redirects = hostname.redirects.slice(0);
      redirects[index] = {
        ...redirects[index],
        ...newValues,
      };

      return { redirects };
    });
  }

  handleDelete = id => (index) => {
    this.changeHostname(id, (hostname) => {
      const redirects = hostname.redirects.slice(0);
      redirects.splice(index, 1);

      return { redirects };
    });
  }

  handleSave = (event) => {
    event.preventDefault();

    const { touchedHostnames, hostnames } = this.state;
    const updates = hostnames.filter(({ id }) => touchedHostnames.indexOf(id) !== -1);

    this.props.onSave(updates);
  };

  touchHostname = (id) => {
    // Let the state know that this redirect was touched
    if (this.state.touchedHostnames.indexOf(id) === -1) {
      this.setState(({ touchedHostnames }) => ({
        touchedHostnames: [...touchedHostnames, id],
      }));
    }
  };

  render() {
    // TODO: Make this more prettier
    if (!this.props.hostnames) {
      return <div>Loading...</div>;
    }

    if (this.props.hostnames.length === 0) {
      return (
        <Authorization action="manage" subject={[SEO_REDIRECTS, BUILDER]}>
          {authorized => (authorized ? (
            <div className={style.heading}>
              You must add a domain before you can add any redirects.
            </div>
          ) : <UnauthorizedBanner />)}
        </Authorization>
      );
    }

    const { isSaving, error } = this.props;
    const { hostnames, showUploadRedirects } = this.state;

    return (
      <Authorization action="manage" subject={[SEO_REDIRECTS, BUILDER]}>
        {authorized => (
          authorized ?
            <div className={style.root}>
              <AsyncFeedback
                isSaving={isSaving}
                success="Your changes have been saved."
                error={error}
              />
              <Button
                className={style.upload}
                context="neutral"
                variant="default"
                onClick={this.toggleUploadRedirects}
                disabled={showUploadRedirects}
              >
             Upload Redirects
              </Button>
              {showUploadRedirects &&
                <RedirectsEditModal
                  show
                  onDismiss={this.toggleUploadRedirects}
                  onUpload={this.handleUpload}
                />}
              <form onSubmit={this.handleSave}>
                {hostnames.map((hostname) => {
                  let title = hostname.hostname;
                  if (hostname.primary) {
                    title += ' (Primary)';
                  }

                  return (
                    <RedirectTable
                      key={`redirect-table-${hostname.id}`}
                      title={title}
                      redirects={hostname.redirects}
                      onAdd={this.handleAdd(hostname.id)}
                      onChange={this.handleChange(hostname.id)}
                      onDelete={this.handleDelete(hostname.id)}
                    />
                  );
                })}
                <Button
                  type="submit"
                  className={style.submitBtn}
                  disabled={isSaving}
                >
                  {isSaving ? 'Saving...' : 'Save'}
                </Button>
              </form>
            </div>
            :
            <UnauthorizedBanner />
        )}
      </Authorization>
    );
  }
}
